<template>
    <div class="p-4 bg-white shadow-md">
        <h2 class="text-xl font-semibold text-gray-800 flex items-center mb-4">
            <span class="text-xl mr-2">📊</span>
            <span>High School Score vs Current GPA</span>
        </h2>
        <div v-if="hasData" class="flex flex-col">
            <div class="space-y-8">
                <!-- High School Scores Tiles -->
                <section>
                    <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                        <span class="mr-3 text-2xl">🏫</span>
                        <span>High School Performance Overview</span>
                    </h2>

                    <!-- Min/Max Score Cards -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div v-for="item in minMaxHsGpaCount" :key="item.hs_gpa"
                            class="relative overflow-hidden rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105"
                            :style="getTileColorForHighSchoolScore(item.hs_gpa)">
                            <div class="relative p-8 text-center text-white">
                                <div class="text-5xl font-bold mb-2">{{ item.hs_gpa }}%</div>
                                <div class="text-lg font-medium opacity-90">
                                    {{item.hs_gpa === Math.max(...minMaxHsGpaCount.map(x => x.hs_gpa)) ? 'Highest Score'
                                        : 'Lowest Score'}}
                                </div>
                                <div class="text-2xl font-bold mt-3">{{ item.count }} Students</div>
                                <div class="text-sm opacity-75 mt-1">
                                    {{ ((item.count / totalStudents) * 100).toFixed(1) }}% of total
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Score Range Cards -->
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div v-for="item in processedHsGpaRangeCount" :key="item.gpa_bucket"
                            class="group relative overflow-hidden rounded-lg shadow-md transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                            :style="getColorClassForHighSchoolScore(item.gpa_bucket)">
                            <div class="relative p-6 text-center text-white">
                                <div class="text-lg font-semibold mb-2">{{ item.gpa_bucket }}</div>
                                <div class="text-3xl font-bold mb-1">{{ item.total_students }}</div>
                                <div class="text-sm opacity-75">Students</div>
                                <div class="mt-2 text-xs opacity-60">
                                    {{ ((item.total_students / totalStudents) * 100).toFixed(1) }}%
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- College GPA Tiles -->
                <section>
                    <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                        <span class="mr-3 text-2xl">🎓</span>
                        <span>College Academic Performance</span>
                    </h2>

                    <!-- Min/Max GPA Cards -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div v-for="item in minMaxGpaCount" :key="item.gpa"
                            class="relative overflow-hidden rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105"
                            :style="getTileColorForCollegeGpa(item.gpa)">
                            <div class="relative p-8 text-center text-white">
                                <div class="text-5xl font-bold mb-2">{{ item.gpa }}</div>
                                <div class="text-lg font-medium opacity-90">
                                    {{item.gpa === Math.max(...minMaxGpaCount.map(x => x.gpa)) ? 'Highest GPA' :
                                        'Lowest GPA'}}
                                </div>
                                <div class="text-2xl font-bold mt-3">{{ item.count }} Students</div>
                                <div class="text-sm opacity-75 mt-1">
                                    {{ ((item.count / totalStudents) * 100).toFixed(1) }}% of total
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- GPA Range Cards -->
                    <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                        <div v-for="item in processedGpaRangeCount" :key="item.gpa_bucket"
                            class="group relative overflow-hidden rounded-lg shadow-md transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                            :style="getColorClassForCollegeGpa(item.gpa_bucket)">
                            <div class="relative p-6 text-center text-white">
                                <div class="text-lg font-semibold mb-2">{{ item.gpa_bucket }}</div>
                                <div class="text-3xl font-bold mb-1">{{ item.total_students }}</div>
                                <div class="text-sm opacity-75">Students</div>
                                <div class="mt-2 text-xs opacity-60">
                                    {{ ((item.total_students / totalStudents) * 100).toFixed(1) }}%
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <div class="mt-8 overflow-x-auto">
                <table class="min-w-full table-auto border-collapse border border-gray-200">
                    <thead>
                        <tr class="bg-gray-100 text-left">
                            <th class="border border-gray-200 px-4 py-2">#</th>
                            <th class="border border-gray-200 px-4 py-2">Student ID</th>
                            <th class="border border-gray-200 px-4 py-2">Name</th>
                            <th class="border border-gray-200 px-4 py-2">Program</th>
                            <th class="border border-gray-200 px-4 py-2">High School Name</th>
                            <th class="border border-gray-200 px-4 py-2">High School Score (%)</th>
                            <th class="border border-gray-200 px-4 py-2">Current GPA</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(student, index) in data" :key="student.student_id" class="hover:bg-gray-50">
                            <td class="border border-gray-200 px-4 py-2">{{ paginatorInfo.firstItem + index }}</td>
                            <td class="border border-gray-200 px-4 py-2">{{ student.student_id }}</td>
                            <td class="border border-gray-200 px-4 py-2">{{ student.name_en }}</td>
                            <td class="border border-gray-200 px-4 py-2">{{ student.program }}</td>
                            <td class="border border-gray-200 px-4 py-2">{{ student.hs_name }}</td>
                            <td class="border border-gray-200 px-4 py-2">{{ formatHsScore(student.hs_score) }}</td>
                            <td class="border border-gray-200 px-4 py-2">{{ formatCurrentGpa(student.gpa) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="mt-4">
                <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>

            <HighSchoolScoreVsCurrentGpaScatter class="mt-8" :data="data" v-if="showChart" />

        </div>
        <div v-else class="text-gray-500 text-center py-16">
            <div class="text-4xl mb-2">⚠️</div>
            <div>There is no data to show</div>
        </div>
    </div>
</template>

<script>
import Pagination from "../../../components/shared/pagination.vue";
import HighSchoolScoreVsCurrentGpaScatter from "../charts/HighSchoolScoreVsCurrentGpaScatter.vue";
export default {
    name: "HighSchoolScoreVsCurrentGpa",
    components: {
        Pagination,
        HighSchoolScoreVsCurrentGpaScatter,
    },
    props: ["data", "paginatorInfo", "minMaxGpaCount", "gpaRangeCount", "minMaxHsGpaCount", "hsGpaRangeCount"],
    data() {
        return {
            showChart: true,
        };
    },
    computed: {
        hasData() {
            return Array.isArray(this.data) && this.data.length > 0;
        },
        totalStudents() {
            if (!this.hsGpaRangeCount || this.hsGpaRangeCount.length === 0) return 0;
            return this.hsGpaRangeCount.reduce((sum, item) => sum + item.male_count + item.female_count, 0);
        },
        processedHsGpaRangeCount() {
            if (!this.hsGpaRangeCount) return [];

            // Define all possible high school score ranges
            const allRanges = [
                '90 - 100',
                '80 - 90',
                '70 - 80',
                '60 - 70',
                '50 - 60',
                '40 - 50',
                '30 - 40',
                '20 - 30',
                '10 - 20',
                '0 - 10'
            ];

            // Create a map of existing data
            const dataMap = {};
            this.hsGpaRangeCount.forEach(item => {
                dataMap[item.gpa_bucket] = item.male_count + item.female_count;
            });

            // Group lower ranges into "< 70%" category
            const processed = [];
            let lessThan70Count = 0;

            allRanges.forEach(range => {
                const totalStudents = dataMap[range] || 0;

                // Check if this is a lower range that should be grouped
                if (range === '60 - 70' || range === '50 - 60' || range === '40 - 50' ||
                    range === '30 - 40' || range === '20 - 30' || range === '10 - 20' || range === '0 - 10') {
                    lessThan70Count += totalStudents;
                } else {
                    // Keep higher ranges as they are
                    processed.push({
                        gpa_bucket: range,
                        male_count: 0,
                        female_count: 0,
                        total_students: totalStudents
                    });
                }
            });

            // Add the grouped "< 70%" category
            processed.push({
                gpa_bucket: '< 70%',
                male_count: 0,
                female_count: 0,
                total_students: lessThan70Count
            });

            return processed;
        },
        processedGpaRangeCount() {
            if (!this.gpaRangeCount) return [];

            // Define all possible GPA ranges
            const allRanges = [
                '3.5 - 4',
                '3 - 3.5',
                '2.5 - 3',
                '2 - 2.5',
                '1.5 - 2',
                '1 - 1.5',
                '0.5 - 1',
                '0 - 0.5'
            ];

            // Create a map of existing data
            const dataMap = {};
            this.gpaRangeCount.forEach(item => {
                dataMap[item.gpa_bucket] = item.male_count + item.female_count;
            });

            // Return all ranges with data or 0
            return allRanges.map(range => ({
                gpa_bucket: range,
                male_count: 0,
                female_count: 0,
                total_students: dataMap[range] || 0
            }));
        }
    },
    methods: {
        formatHsScore(value) {
            const num = parseFloat(value);
            return isNaN(num) ? '-' : num;
        },
        formatCurrentGpa(value) {
            const num = parseFloat(value);
            return isNaN(num) ? '-' : num;
        },
        setPage(page) {
            this.$emit('set-page', page);
        },
        getTileColorForCollegeGpa(gpa) {
            const numGpa = parseFloat(gpa);
            if (isNaN(numGpa)) return "background: linear-gradient(135deg, #718096, #a0aec0)"; // gray for invalid
            if (numGpa >= 3.5) return "background: linear-gradient(135deg, #38b2ac, #81e6d9)";
            if (numGpa >= 3) return "background: linear-gradient(135deg, #4299e1, #90cdf4)";
            if (numGpa >= 2) return "background: linear-gradient(135deg, #ecc94b, #f6e05e)";
            return "background: linear-gradient(135deg, #f56565, #feb2b2)";
        },
        getColorClassForCollegeGpa(range) {
            switch (range) {
                case '3.5 - 4': return 'background: linear-gradient(135deg, #38b2ac, #81e6d9)';
                case '3 - 3.5': return 'background: linear-gradient(135deg, #4299e1, #90cdf4)';
                case '2.5 - 3': return 'background: linear-gradient(135deg, #ecc94b, #f6e05e)';
                case '2 - 2.5': return 'background: linear-gradient(135deg, #ed8936, #fbd38d)';
                case '1.5 - 2': return 'background: linear-gradient(135deg, #f56565, #feb2b2)';
                case '1 - 1.5': return 'background: linear-gradient(135deg, #c53030, #fc8181)';
                case '0.5 - 1': return 'background: linear-gradient(135deg, #9b2c2c, #fca5a5)';
                case '0 - 0.5': return 'background: linear-gradient(135deg, #742a2a, #f87171)';
                default: return 'background: linear-gradient(135deg, #f56565, #feb2b2)';
            }
        },
        getTileColorForHighSchoolScore(score) {
            const numScore = parseFloat(score);
            if (isNaN(numScore)) return 'background: linear-gradient(135deg, #718096, #a0aec0)'; // gray for invalid
            if (numScore >= 90) return 'background: linear-gradient(135deg, #38b2ac, #81e6d9)';
            if (numScore >= 75) return 'background: linear-gradient(135deg, #4299e1, #90cdf4)';
            if (numScore >= 60) return 'background: linear-gradient(135deg, #ecc94b, #f6e05e)';
            return 'background: linear-gradient(135deg, #f56565, #feb2b2)';
        },
        getColorClassForHighSchoolScore(range) {
            switch (range) {
                case '90 - 100':
                    return 'background: linear-gradient(135deg, #38b2ac, #81e6d9)'; // teal
                case '80 - 90':
                    return 'background: linear-gradient(135deg, #4299e1, #90cdf4)'; // blue
                case '70 - 80':
                    return 'background: linear-gradient(135deg, #ecc94b, #f6e05e)'; // yellow
                case '< 70%':
                    return 'background: linear-gradient(135deg, #f56565, #feb2b2)'; // red for lower scores
                default:
                    return 'background: linear-gradient(135deg, #718096, #a0aec0)'; // gray for undefined
            }
        },
    },
};
</script>